var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useEffect, useRef } from "react";
import { CellType, MatrixConditionKeyEnum, SurveyElementTypeEnum, } from "../../../types/forms";
import { evaluateElementConditions } from "../../../utils/elements-condition";
import { applyMatrixColumnResetToMatrixDropdown, applyMatrixColumnResetToMatrixDynamic, buildColumnTrackedSignature, extractFieldsNamesFromExpression, flattenPageElements, getInitialValueForElement, getMatrixColumnsConditionStatus, getMatrixVisibleColumns, isValueProducingElement, resolveSetValueExpression, } from "../../../utils/forms-functions";
import { getElementValueKey } from "../../../utils/helpers";
import { getDefaultCellValue } from "../utils/MatrixCellRenderers";
var normalizeValueForComparison = function (value) {
    if (Array.isArray(value)) {
        return __spreadArray([], value, true).map(function (item) { return String(item); }).sort();
    }
    return value;
};
var buildElementTrackedSignature = function (expression, formValues) {
    if (!expression) {
        return "";
    }
    var trackedFieldNames = extractFieldsNamesFromExpression(expression);
    if (!trackedFieldNames.length) {
        return "";
    }
    var signaturePayload = trackedFieldNames.map(function (fieldName) { return ({
        fieldName: fieldName,
        value: normalizeValueForComparison(formValues[fieldName]),
    }); });
    return JSON.stringify(signaturePayload);
};
export var useGlobalElementConditionEffects = function (_a) {
    var pages = _a.pages, formValues = _a.formValues, elementsTypeInfoByName = _a.elementsTypeInfoByName, setValue = _a.setValue;
    var stateRef = useRef(new Map());
    var columnConditionStateRef = useRef(new Map());
    var didInitializeRef = useRef(false);
    var pagesSnapshotRef = useRef("");
    useEffect(function () {
        var pagesKey = pages
            .map(function (page) {
            return flattenPageElements(page.elements)
                .map(function (element) { return element.name; })
                .join(",");
        })
            .join("|");
        if (pagesSnapshotRef.current !== pagesKey) {
            pagesSnapshotRef.current = pagesKey;
            stateRef.current.clear();
            columnConditionStateRef.current.clear();
            didInitializeRef.current = false;
        }
        var allElements = pages
            .flatMap(function (page) { return flattenPageElements(page.elements); })
            .filter(isValueProducingElement);
        var state = stateRef.current;
        var pageEnabledFallback = true;
        if (!didInitializeRef.current) {
            allElements.forEach(function (element) {
                var _a = evaluateElementConditions(element, formValues, elementsTypeInfoByName, pageEnabledFallback), shouldResetValue = _a.shouldResetValue, shouldSetValue = _a.shouldSetValue;
                state.set(element.name, {
                    prevShouldReset: shouldResetValue,
                    prevShouldSet: shouldSetValue,
                    prevTrackedResetSig: buildElementTrackedSignature(element.resetValueIf, formValues),
                    prevTrackedSetSig: buildElementTrackedSignature(element.setValueIf, formValues),
                });
            });
            allElements.forEach(function (element) {
                if (element.type !== SurveyElementTypeEnum.MATRIXDROPDOWN &&
                    element.type !== SurveyElementTypeEnum.MATRIXDYNAMIC) {
                    return;
                }
                var columns = (element.columns ||
                    []);
                var visibleColumns = getMatrixVisibleColumns(columns, formValues, elementsTypeInfoByName);
                var columnsToReset = getMatrixColumnsConditionStatus({
                    visibleColumns: visibleColumns,
                    formValues: formValues,
                    elementsTypeInfoByName: elementsTypeInfoByName,
                    conditionKey: MatrixConditionKeyEnum.RESET_VALUE_IF,
                });
                var columnsToSet = getMatrixColumnsConditionStatus({
                    visibleColumns: visibleColumns,
                    formValues: formValues,
                    elementsTypeInfoByName: elementsTypeInfoByName,
                    conditionKey: MatrixConditionKeyEnum.SET_VALUE_IF,
                });
                visibleColumns.forEach(function (col) {
                    var _a, _b;
                    if (col.resetValueIf === undefined && col.setValueIf === undefined) {
                        return;
                    }
                    var key = "".concat(element.name, "::").concat(col.name);
                    columnConditionStateRef.current.set(key, {
                        prevShouldReset: (_a = columnsToReset[col.name]) !== null && _a !== void 0 ? _a : false,
                        prevShouldSet: (_b = columnsToSet[col.name]) !== null && _b !== void 0 ? _b : false,
                        prevTrackedResetSig: buildColumnTrackedSignature(col.resetValueIf, formValues),
                        prevTrackedSetSig: buildColumnTrackedSignature(col.setValueIf, formValues),
                    });
                });
            });
            didInitializeRef.current = true;
            return;
        }
        allElements.forEach(function (element) {
            var _a = evaluateElementConditions(element, formValues, elementsTypeInfoByName, pageEnabledFallback), shouldResetValue = _a.shouldResetValue, shouldSetValue = _a.shouldSetValue;
            var resetSig = buildElementTrackedSignature(element.resetValueIf, formValues);
            var setSig = buildElementTrackedSignature(element.setValueIf, formValues);
            var elState = state.get(element.name);
            if (!elState) {
                elState = {
                    prevShouldReset: shouldResetValue,
                    prevShouldSet: shouldSetValue,
                    prevTrackedResetSig: resetSig,
                    prevTrackedSetSig: setSig,
                };
                state.set(element.name, elState);
                return;
            }
            var elementValueKey = getElementValueKey(element);
            if (element.resetValueIf) {
                var conditionTurnedTrue = !elState.prevShouldReset && shouldResetValue;
                var trackedValuesChanged = elState.prevTrackedResetSig !== resetSig;
                if (conditionTurnedTrue || (shouldResetValue && trackedValuesChanged)) {
                    setValue(elementValueKey, getInitialValueForElement(element), { shouldValidate: false, shouldDirty: true });
                }
                elState.prevShouldReset = shouldResetValue;
                elState.prevTrackedResetSig = resetSig;
            }
            if (element.setValueIf) {
                var conditionTurnedTrue = !elState.prevShouldSet && shouldSetValue;
                var trackedValuesChanged = elState.prevTrackedSetSig !== setSig;
                if (conditionTurnedTrue || (shouldSetValue && trackedValuesChanged)) {
                    if (element.setValueExpression === undefined ||
                        element.setValueExpression === "") {
                        elState.prevShouldSet = shouldSetValue;
                        elState.prevTrackedSetSig = setSig;
                        return;
                    }
                    var resolvedValue = resolveSetValueExpression(element.setValueExpression);
                    if (!resolvedValue) {
                        elState.prevShouldSet = shouldSetValue;
                        elState.prevTrackedSetSig = setSig;
                        return;
                    }
                    setValue(elementValueKey, resolvedValue, {
                        shouldValidate: false,
                        shouldDirty: true,
                    });
                }
                elState.prevShouldSet = shouldSetValue;
                elState.prevTrackedSetSig = setSig;
            }
        });
        allElements.forEach(function (element) {
            var _a, _b, _c, _d;
            if (element.type !== SurveyElementTypeEnum.MATRIXDROPDOWN &&
                element.type !== SurveyElementTypeEnum.MATRIXDYNAMIC) {
                return;
            }
            var columns = (element.columns || []);
            var visibleColumns = getMatrixVisibleColumns(columns, formValues, elementsTypeInfoByName);
            var columnsWithConditions = visibleColumns.filter(function (column) {
                return column.resetValueIf !== undefined || column.setValueIf !== undefined;
            });
            if (!columnsWithConditions.length) {
                return;
            }
            var columnsToResetByResetValueIf = getMatrixColumnsConditionStatus({
                visibleColumns: visibleColumns,
                formValues: formValues,
                elementsTypeInfoByName: elementsTypeInfoByName,
                conditionKey: MatrixConditionKeyEnum.RESET_VALUE_IF,
            });
            var columnsToSetBySetValueIf = getMatrixColumnsConditionStatus({
                visibleColumns: visibleColumns,
                formValues: formValues,
                elementsTypeInfoByName: elementsTypeInfoByName,
                conditionKey: MatrixConditionKeyEnum.SET_VALUE_IF,
            });
            var elementKeyValue = getElementValueKey(element);
            var elementCellType = element.cellType || CellType.DEFAULT;
            var rows = (element.rows || []);
            var currentValue = formValues[elementKeyValue];
            var dirty = false;
            for (var _i = 0, columnsWithConditions_1 = columnsWithConditions; _i < columnsWithConditions_1.length; _i++) {
                var col = columnsWithConditions_1[_i];
                var key = "".concat(element.name, "::").concat(col.name);
                var columnCellType = col.cellType || elementCellType;
                var columnResetSignature = buildColumnTrackedSignature(col.resetValueIf, formValues);
                var columnSetSignature = buildColumnTrackedSignature(col.setValueIf, formValues);
                var colState = columnConditionStateRef.current.get(key);
                if (!colState) {
                    columnConditionStateRef.current.set(key, {
                        prevShouldReset: (_a = columnsToResetByResetValueIf[col.name]) !== null && _a !== void 0 ? _a : false,
                        prevShouldSet: (_b = columnsToSetBySetValueIf[col.name]) !== null && _b !== void 0 ? _b : false,
                        prevTrackedResetSig: columnResetSignature,
                        prevTrackedSetSig: columnSetSignature,
                    });
                    continue;
                }
                if (col.resetValueIf) {
                    var shouldReset = (_c = columnsToResetByResetValueIf[col.name]) !== null && _c !== void 0 ? _c : false;
                    var conditionTurnedTrue = !colState.prevShouldReset && shouldReset;
                    var trackedValuesChanged = colState.prevTrackedResetSig !== columnResetSignature;
                    if (conditionTurnedTrue || (shouldReset && trackedValuesChanged)) {
                        var defaultCell = getDefaultCellValue(columnCellType);
                        if (element.type === SurveyElementTypeEnum.MATRIXDYNAMIC) {
                            currentValue = applyMatrixColumnResetToMatrixDynamic(currentValue, col.name, defaultCell);
                        }
                        else {
                            currentValue = applyMatrixColumnResetToMatrixDropdown(currentValue, rows, col.name, defaultCell);
                        }
                        dirty = true;
                    }
                    colState.prevShouldReset = shouldReset;
                    colState.prevTrackedResetSig = columnResetSignature;
                }
                if (col.setValueIf) {
                    var shouldSet = (_d = columnsToSetBySetValueIf[col.name]) !== null && _d !== void 0 ? _d : false;
                    var conditionTurnedTrue = !colState.prevShouldSet && shouldSet;
                    var trackedValuesChanged = colState.prevTrackedSetSig !== columnSetSignature;
                    if (conditionTurnedTrue || (shouldSet && trackedValuesChanged)) {
                        if (col.setValueExpression === undefined ||
                            col.setValueExpression === "") {
                            colState.prevShouldSet = shouldSet;
                            colState.prevTrackedSetSig = columnSetSignature;
                            return;
                        }
                        var resolvedValue = resolveSetValueExpression(col.setValueExpression);
                        if (!resolvedValue) {
                            colState.prevShouldSet = shouldSet;
                            colState.prevTrackedSetSig = columnSetSignature;
                            return;
                        }
                        if (element.type === SurveyElementTypeEnum.MATRIXDYNAMIC) {
                            currentValue = applyMatrixColumnResetToMatrixDynamic(currentValue, col.name, resolvedValue);
                        }
                        else {
                            currentValue = applyMatrixColumnResetToMatrixDropdown(currentValue, rows, col.name, resolvedValue);
                        }
                        dirty = true;
                    }
                    colState.prevShouldSet = shouldSet;
                    colState.prevTrackedSetSig = columnSetSignature;
                }
            }
            if (dirty) {
                setValue(elementKeyValue, currentValue, {
                    shouldValidate: false,
                    shouldDirty: true,
                });
            }
        });
    }, [pages, formValues, elementsTypeInfoByName, setValue]);
};
